import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

export default function CustomTemplatesPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://funtimephotobooths.com';

  useSEO({
    title: 'Custom Photo Booth Templates San Diego | Fun Time Photobooths',
    description: 'Personalized photo booth templates for your San Diego event. Custom designs with your branding, logos, event details, and themed layouts. Make your photos unforgettable.',
    keywords: 'custom photo templates San Diego, personalized photo booth, event branding, photo booth design, custom layouts',
    canonical: `${siteUrl}/custom-templates`,
    ogTitle: 'Custom Photo Booth Templates | Fun Time Photobooths',
    ogDescription: 'Create personalized photo booth templates with your branding, logos, and event details for memorable keepsakes.',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Custom Photo Booth Templates',
      description: 'Personalized photo booth template design services for events in San Diego, featuring custom branding, logos, and themed layouts.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Fun Time Photobooths',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'San Diego',
          addressRegion: 'CA',
          addressCountry: 'US',
        },
      },
      areaServed: {
        '@type': 'City',
        name: 'San Diego',
      },
      url: `${siteUrl}/custom-templates`,
    },
  });

  const templateExamples = [
    {
      title: 'Wedding Templates',
      description: 'Elegant designs featuring your names, wedding date, and romantic themes',
      icon: 'ri-hearts-line',
    },
    {
      title: 'Corporate Branding',
      description: 'Professional layouts with your company logo and brand colors',
      icon: 'ri-building-line',
    },
    {
      title: 'Birthday Celebrations',
      description: 'Fun and colorful designs perfect for milestone birthdays',
      icon: 'ri-cake-3-line',
    },
    {
      title: 'Holiday Themes',
      description: 'Seasonal templates for Christmas, Halloween, and special occasions',
      icon: 'ri-gift-line',
    },
    {
      title: 'Graduation Events',
      description: 'Academic-themed designs celebrating achievement',
      icon: 'ri-graduation-cap-line',
    },
    {
      title: 'Custom Designs',
      description: 'Completely unique templates tailored to your vision',
      icon: 'ri-palette-line',
    },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-orange-50 to-orange-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Custom Photo Templates
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Make every photo a personalized keepsake with custom-designed templates featuring your event details, branding, and unique style
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Design Your Template
            </Link>
          </div>
        </div>
      </section>

      {/* Template Widget */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Browse Our Template Gallery
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore hundreds of customizable templates for your event. Each design can be personalized with your colors, text, and branding.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-4 shadow-sm">
            <iframe
              src="https://templatesbooth.com/widget-embed/?key=NTkw"
              scrolling="yes"
              width="100%"
              height="2200px"
              frameBorder="0"
              title="Custom Photo Booth Templates Gallery"
              className="rounded-lg"
            />
          </div>
        </div>
      </section>

      {/* Template Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Template Options
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose from our popular template styles or create something completely unique
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {templateExamples.map((template, index) => (
              <div key={index} className="bg-white rounded-xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <i className={`${template.icon} text-3xl text-orange-500`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {template.title}
                </h3>
                <p className="text-gray-600">
                  {template.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customization Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What You Can Customize
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every element of your photo template can be personalized to match your event
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-xl p-6 flex items-start gap-4">
                <i className="ri-text text-2xl text-orange-500 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Event Details</h4>
                  <p className="text-gray-600">Names, dates, hashtags, and special messages</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 flex items-start gap-4">
                <i className="ri-image-line text-2xl text-orange-500 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Logos & Graphics</h4>
                  <p className="text-gray-600">Company logos, monograms, and custom artwork</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 flex items-start gap-4">
                <i className="ri-palette-line text-2xl text-orange-500 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Color Schemes</h4>
                  <p className="text-gray-600">Match your brand colors or event theme</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 flex items-start gap-4">
                <i className="ri-layout-line text-2xl text-orange-500 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Layout Styles</h4>
                  <p className="text-gray-600">Choose from various photo arrangements</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 flex items-start gap-4">
                <i className="ri-font-size text-2xl text-orange-500 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Typography</h4>
                  <p className="text-gray-600">Select fonts that match your style</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 flex items-start gap-4">
                <i className="ri-contrast-line text-2xl text-orange-500 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Backgrounds</h4>
                  <p className="text-gray-600">Solid colors, patterns, or custom images</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our simple design process ensures your templates are perfect for your event
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Share Your Vision
                  </h3>
                  <p className="text-gray-600">
                    Tell us about your event, provide logos, colors, and any design preferences. Share inspiration images or let us guide you.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    We Design Your Template
                  </h3>
                  <p className="text-gray-600">
                    Our design team creates a custom template mockup based on your specifications. We'll incorporate all your branding elements and event details.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Review & Revise
                  </h3>
                  <p className="text-gray-600">
                    Review your custom design and request any changes. We'll refine the template until it's exactly what you envisioned.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Ready for Your Event
                  </h3>
                  <p className="text-gray-600">
                    Your approved template is loaded into the photo booth system, ready to create beautiful, personalized keepsakes for your guests.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Let's Design Your Perfect Template
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Contact us to discuss your custom template design and make your photos truly memorable
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-white text-orange-500 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Started
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
